import React, { useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Share2, Copy, Check, X, Facebook, Twitter, Linkedin, 
  Mail, MessageCircle, Link2
} from 'lucide-react';

export default function ShareButtons({ contentType, contentId, title, description }) {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const createShareLink = async () => {
    if (!token) {
      alert('Please log in to share content');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/share/create?content_type=${contentType}&content_id=${contentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fullUrl = `${window.location.origin}${response.data.share_url}`;
      setShareUrl(fullUrl);
      setShowModal(true);
    } catch (error) {
      console.error('Failed to create share link:', error);
      // Fall back to current URL
      setShareUrl(window.location.href);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackShare('copy');
  };

  const trackShare = async (platform) => {
    try {
      const shortCode = shareUrl.split('/share/')[1];
      if (shortCode) {
        await axios.post(`${API}/share/${shortCode}/track?platform=${platform}`);
      }
    } catch (error) {
      console.error('Failed to track share:', error);
    }
  };

  const shareOptions = [
    {
      name: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(title)}`
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      url: `https://wa.me/?text=${encodeURIComponent(`${title} - ${shareUrl}`)}`
    },
    {
      name: 'Email',
      icon: Mail,
      color: currentColors.primary,
      url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check this out: ${shareUrl}`)}`
    }
  ];

  return (
    <>
      <button
        onClick={createShareLink}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:opacity-80"
        style={{ 
          backgroundColor: `${currentColors.secondary}20`,
          color: currentColors.secondary
        }}
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4" />
        <span>Share</span>
      </button>

      {/* Share Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />
          <div 
            className="relative w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: currentColors.background, border: `1px solid ${currentColors.border}` }}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 p-2 rounded-lg hover:opacity-80"
              style={{ color: currentColors.textMuted }}
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
              Share Content
            </h3>
            <p className="text-sm mb-6 line-clamp-2" style={{ color: currentColors.textMuted }}>
              {title}
            </p>

            {/* Copy Link */}
            <div className="flex gap-2 mb-6">
              <div 
                className="flex-1 px-4 py-2 rounded-lg text-sm truncate"
                style={{ backgroundColor: `${currentColors.textMuted}10`, color: currentColors.text }}
              >
                {shareUrl}
              </div>
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 rounded-lg transition-all"
                style={{ 
                  backgroundColor: copied ? 'rgba(34, 197, 94, 0.2)' : `${currentColors.primary}20`,
                  color: copied ? '#22c55e' : currentColors.primary
                }}
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>

            {/* Social Buttons */}
            <div className="grid grid-cols-5 gap-3">
              {shareOptions.map(option => (
                <a
                  key={option.name}
                  href={option.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare(option.name.toLowerCase())}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg transition-all hover:scale-105"
                  style={{ backgroundColor: `${option.color}15` }}
                  title={`Share on ${option.name}`}
                >
                  <option.icon className="w-6 h-6" style={{ color: option.color }} />
                  <span className="text-xs" style={{ color: currentColors.textMuted }}>
                    {option.name}
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-6 pt-4" style={{ borderTop: `1px solid ${currentColors.border}` }}>
              <p className="text-xs text-center" style={{ color: currentColors.textMuted }}>
                Share the cosmic wisdom with your friends and community
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
